import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './DocumentDetailView.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSpinner, faArchive, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons';
import {
  useAuth,
  UserAbility,
  useDocumentViewers,
  PATIENT_DIARY_LAYOUT_SIDEBAR,
  PATIENT_DIARY_LAYOUT_TAB,
  DOCUMENT_TAB_VIEW_CODES,
  DOCUMENT_TAB_VIEW_ALL,
} from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';
import DetailsTab from './tabs/DetailsTab';
import DataTab from './tabs/DataTab';
import MeasurementsTab from './tabs/MeasurementsTab';
import OutcomeTasksTab from './tabs/OutcomeTasksTab';
import ActionsTab from './tabs/ActionsTab';
import MissingDocumentView, { DOCUMENT_MISSING_REASON, DocumentLoadingView } from './MissingDocumentView';
import PatientDiaryPanel from './PatientDiaryPanel';
import FileAwayModal from './FileAwayModal';
import useShouldBundleBloodPressureReadings from './measurements/useShouldBundleBloodPressureReadings';
import ViewersWarningModal from './ViewersWarningModal';
import EmailForwardWarningModal from './EmailForwardWarningModal';
import ReferralModal from '../referrals/ReferralModal';
import PendingReferralsModal from './PendingReferralsModal';
import PatientStatusWarningModal from './PatientStatusWarningModal';
import DeleteModal from './DeleteModal';
import DocumentToolbar from './DocumentToolbar';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import CommentsPanel from '../components/CommentsPanel';
import WorkflowLogsModal from '../workflow/components/WorkflowLogsModal';
import WorkflowPreviewModal from '../workflow/components/WorkflowPreviewModal';
import useDocumentRecord from './useDocumentRecord';
import useDocumentPatient from './useDocumentPatient';
import usePendingReferralModal from './usePendingReferralModal';
import useDocumentFileAway from './useDocumentFileAway';
import useDocumentDuplicates from './useDocumentDuplicates';
import useDocumentWorkflow from './useDocumentWorkflow';
import useDocumentEntryWarnings from './useDocumentEntryWarnings';
import useDocumentTitle from './useDocumentTitle';
import useDocumentAssignee from './useDocumentAssignee';
import useDocumentSidebar from './useDocumentSidebar';
import useDocumentNavigation from './useDocumentNavigation';
import useDocumentViewer from './useDocumentViewer';
import useDocumentLiveUpdates from './useDocumentLiveUpdates';
import '@medprompt/shared/src/common/CommonStyles.css';
import '@medprompt/shared/src/common/modals.css';

const SidebarTabPane = ({ title, visible, paneRef, children }) => (
  <div className={`sidebar-tab-pane${visible ? ' is-visible' : ''}`}>
    {title && (
      <div className="sidebar-tab-pane-title" title={title}>{title}</div>
    )}
    <div className="sidebar-tab-pane-body" ref={paneRef}>
      {children}
    </div>
  </div>
);

const DocumentDetailView = ({ viewerContext }) => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const shouldBundleBloodPressureReadings = useShouldBundleBloodPressureReadings();
  const { getViewerCount, getViewers } = useDocumentViewers();
  const { hasViewers } = viewerContext || {};

  const record = useDocumentRecord({ documentId, user });
  const { doc, documentService, loadKey, getDocScopeId, docScopeOptions, updateDoc } = record;

  const patient = useDocumentPatient({ documentId, doc, documentService, docScopeOptions, loadKey });
  const referral = usePendingReferralModal({
    loadKey,
    removePendingReferralLocally: patient.removePendingReferralLocally,
    applyPendingReferralPatch: patient.applyPendingReferralPatch,
    dismissPendingReferrals: patient.dismissPendingReferrals,
  });
  const fileAway = useDocumentFileAway({ doc, documentService, updateDoc, getDocScopeId, docScopeOptions, loadKey });
  const duplicates = useDocumentDuplicates({ doc, documentService, getDocScopeId, docScopeOptions, loadKey });
  const workflow = useDocumentWorkflow({
    documentId, doc, user, location, navigate, getDocScopeId, docScopeOptions, loadKey,
  });
  const warnings = useDocumentEntryWarnings({ doc, user, getViewers, loadKey });
  const title = useDocumentTitle({
    doc, isEditingEnabled: record.isEditingEnabled, documentService, updateDoc, loadKey,
  });
  const assignee = useDocumentAssignee({
    doc,
    user,
    activeUsers: record.activeUsers,
    teams: record.teams,
    documentService,
    insightsService: record.insightsService,
    updateDoc,
  });
  const sidebar = useDocumentSidebar({
    doc, location, documentService, updateDoc, isEditingEnabled: record.isEditingEnabled, loadKey,
  });
  const navigation = useDocumentNavigation({
    documentId,
    location,
    navigate,
    backPatientId: patient.patientData?.patientId,
    showWorkflowLogsModal: workflow.showWorkflowLogsModal,
    workflowPreviewModalOpen: workflow.workflowPreviewModalOpen,
    selectedWorkflow: workflow.selectedWorkflow,
    loadKey,
  });
  const viewer = useDocumentViewer({
    documentId, doc, documentService, isEditingEnabled: record.isEditingEnabled,
  });
  useDocumentLiveUpdates({
    documentId,
    doc,
    user,
    documentService,
    insightsService: record.insightsService,
    updateDoc,
    showDeletedPlaceholder: record.showDeletedPlaceholder,
    onFiledAwayByCurrentUser: navigation.requestAutoNavigate,
    refetchDocument: record.refetchDocument,
    refetchPatient: patient.refetchPatient,
  });

  const { activeUsers, teams, isEditingEnabled, isRegenerationEnabled, handleRegenerationClick } = record;
  const { activeTab, setActiveTab, tabViewMode, patientDiaryLayout } = sidebar;
  const pendingReferralsCount = patient.pendingReferralsCount;

  if (record.loading) {
    return <DocumentLoadingView />;
  }

  if (!doc) {
    return (
      <MissingDocumentView
        reason={record.missingReason || DOCUMENT_MISSING_REASON.NEVER_EXISTED}
        backLabel={navigation.backLabel}
        onBack={navigation.handleBackToDocumentsList}
        navigationContext={navigation.navigationContext}
        onNavigatePrev={navigation.handleNavigatePrev}
        onNavigateNext={navigation.handleNavigateNext}
        prevButtonAnimating={navigation.prevButtonAnimating}
        nextButtonAnimating={navigation.nextButtonAnimating}
        documentId={documentId}
        activeUsers={activeUsers}
        teams={teams}
        showAuditTrail={record.showMissingAuditModal}
        onViewAuditTrail={record.openMissingAuditTrail}
        onCloseAuditTrail={record.closeMissingAuditTrail}
        onRetry={record.retryLoad}
      />
    );
  }

  const editingEnabled = isEditingEnabled();
  const hasDocsWrite = user?.authorities?.includes(UserAbility.DOCS_WRITE.key);
  const isAllTabView = tabViewMode === DOCUMENT_TAB_VIEW_ALL;
  const isCodesTabView = tabViewMode === DOCUMENT_TAB_VIEW_CODES;
  const showPatientDiaryTab = patientDiaryLayout === PATIENT_DIARY_LAYOUT_TAB;
  const isAllContentView = isAllTabView && activeTab !== 'Actions';
  const isSplitLayout = isAllContentView || (isCodesTabView && activeTab === 'Codes');
  const isPaneVisible = (paneId) => {
    if (isAllContentView) {
      return paneId !== 'Actions';
    }
    if (isCodesTabView && (paneId === 'ExistingCodes' || paneId === 'NewCodes')) {
      return activeTab === 'Codes';
    }
    return activeTab === paneId;
  };
  const splitTitle = (paneId, label) => (isSplitLayout && isPaneVisible(paneId) ? label : null);
  const detailsNoticeCount =
    (doc.lmcContractViolations?.length || 0) + (doc.isMultiplePatients ? 1 : 0);
  const duplicateDocumentIds = duplicates.duplicateDocumentIds;
  const canDelete = hasDocsWrite && (doc.status === StatusEnum.READY.value || doc.status === StatusEnum.INVALID.value);
  const showFileAwayPatientHint = !record.canFileAway && editingEnabled && !fileAway.fileAwayLoading;

  return (
    <div className="document-detail-view">
      <div className="document-header">
        <div className="header-left">
        <button className="header-button" onClick={navigation.handleBackToDocumentsList}>
          <FontAwesomeIcon icon={faArrowLeft} /> {navigation.backLabel}
        </button>
        {title.isEditingTitle ? (
          <div className="document-title-wrapper">
            <input
              ref={title.titleInputRef}
              type="text"
              className="document-title document-title-input"
              value={title.editedTitle}
              onChange={title.handleTitleChange}
              onBlur={title.handleTitleBlur}
              onKeyDown={title.handleTitleKeyDown}
              style={{ width: `${Math.max(title.editedTitle.length * 8 + 40, 150)}px` }}
            />
            <span className="document-title-extension">
              {title.titleExtension}
            </span>
          </div>
        ) : (
          <div
            className={`document-title ${editingEnabled ? 'document-title-editable' : ''}`}
            onClick={title.handleTitleClick}
            title={editingEnabled ? "Click to edit title" : ""}
          >
            {title.title}
          </div>
        )}
        </div>
        <div className="document-actions">
          {duplicateDocumentIds.length > 0 && (
            <button
              className="header-button header-button-warning"
              onClick={duplicates.openDuplicatesModal}
              title={`View ${duplicateDocumentIds.length} potential duplicate${duplicateDocumentIds.length > 1 ? 's' : ''}`}
            >
              <FontAwesomeIcon icon={faCopy} /> View Duplicates ({duplicateDocumentIds.length})
            </button>
          )}
          <button
            className="header-button header-button-delete"
            onClick={record.openDeleteModal}
            disabled={!canDelete}
            title={!hasDocsWrite ? "You do not have permission to delete documents" : doc.status === StatusEnum.INVALID.value ? "Delete Invalid Document" : "Delete Document"}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
          {doc.status !== StatusEnum.FILED_AWAY.value && (
            <div className={`file-away-button-wrapper ${showFileAwayPatientHint ? 'file-away-disabled-patient' : ''}`}>
              <button
                className="header-button header-button-primary"
                onClick={fileAway.handleFileAway}
                disabled={!editingEnabled || !record.canFileAway || fileAway.fileAwayLoading}
              >
                {fileAway.fileAwayLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faArchive} />
                )} File Away
              </button>
              {showFileAwayPatientHint && (
                <div className="file-away-tooltip">Cannot file away - document not matched to patient</div>
              )}
            </div>
          )}
        </div>
      </div>

      <DocumentToolbar
        doc={doc}
        user={user}
        activeUsers={activeUsers}
        hasViewers={hasViewers}
        getViewerCount={getViewerCount}
        getViewers={getViewers}
        assignee={assignee.assignee}
        assigneeSelectedIcon={assignee.selectedIcon}
        assigneeSelectedOutOfOffice={assignee.selectedOutOfOffice}
        assigneeOptions={assignee.assigneeOptions}
        assigneeDropdownOpen={assignee.assigneeDropdownOpen}
        onToggleAssigneeDropdown={assignee.toggleAssigneeDropdown}
        onAssigneeChange={assignee.handleAssigneeChange}
        canEditAssignee={assignee.canEditAssignee}
        canEditRiskCategory={record.canEditRiskCategory}
        onRiskCategoryClick={record.handleRiskCategoryCycle}
        isCommentsVisible={sidebar.isCommentsVisible}
        onToggleComments={sidebar.toggleCommentsVisible}
        showWorkflowRunsButton={workflow.canReadConfig && workflow.workflowLogsCount > 0}
        workflowLogsCount={workflow.workflowLogsCount}
        onShowWorkflowLogs={workflow.openWorkflowLogs}
        isPatientDiaryVisible={sidebar.isPatientDiaryVisible}
        onTogglePatientDiary={sidebar.togglePatientDiaryVisible}
        showPatientDiaryToggle={patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR}
        pendingReferralsCount={pendingReferralsCount}
        navigationContext={navigation.navigationContext}
        onNavigatePrev={navigation.handleNavigatePrev}
        onNavigateNext={navigation.handleNavigateNext}
        prevButtonAnimating={navigation.prevButtonAnimating}
        nextButtonAnimating={navigation.nextButtonAnimating}
        isRegenerationEnabled={isRegenerationEnabled}
        handleRegenerationClick={handleRegenerationClick}
      />

      {/* Comments Panel - positioned outside DocumentToolbar to prevent re-rendering issues */}
      <CommentsPanel
        comments={doc.comments || []}
        activeUsers={activeUsers}
        currentUser={user}
        isVisible={sidebar.isCommentsVisible}
        onClose={sidebar.closeComments}
        onAddComment={record.handleAddComment}
      />

      <WorkflowLogsModal
        isOpen={workflow.showWorkflowLogsModal}
        onClose={workflow.closeWorkflowLogs}
        documentId={documentId}
        onWorkflowClick={workflow.openWorkflowPreview}
      />

      <WorkflowPreviewModal
        isOpen={workflow.workflowPreviewModalOpen}
        onClose={workflow.closeWorkflowPreview}
        workflow={workflow.selectedWorkflow}
        onEdit={workflow.editWorkflow}
        hasEditPermission={user?.authorities?.includes(UserAbility.CONFIG_WRITE.key)}
        activeUsers={activeUsers}
        teams={teams}
      />

      <div className={`document-content ${patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR && sidebar.isPatientDiaryVisible ? 'three-panel' : 'two-panel'}${isAllContentView ? ' tab-view-all' : ''}${isCodesTabView && activeTab === 'Codes' ? ' tab-view-codes' : ''}`}>
        <div className="document-viewer">
          <div className="document-container">
            {!viewer.pdfViewerReady && (
              <div className="pdf-loading-overlay">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading PDF viewer...</span>
              </div>
            )}
            <div
              key={documentId} // Force recreate element when document changes
              ref={viewer.viewerRef}
              style={{
                width: '100%',
                height: '100%',
                minHeight: '600px',
                opacity: viewer.pdfViewerReady ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}
            />
          </div>
        </div>

        <div className="detail-sidebar">
          {isAllTabView ? (
          <div className="doc-tabs">
            <div
              className={`doc-tab ${activeTab !== 'Actions' ? 'active' : ''}`}
              onClick={() => setActiveTab('Content')}
            >
              Content
              {detailsNoticeCount > 0 && (
                <span className="notification-badge">{detailsNoticeCount}</span>
              )}
            </div>
            <div
              className={`doc-tab ${activeTab === 'Actions' ? 'active' : ''}`}
              onClick={() => setActiveTab('Actions')}
            >
              Actions
            </div>
          </div>
          ) : (
          <div className="doc-tabs">
            <div
              className={`doc-tab ${activeTab === 'Details' ? 'active' : ''}${sidebar.detailsTabAnimating ? ' doc-tab-animating' : ''}`}
              onClick={() => setActiveTab('Details')}
            >
              Details
              {detailsNoticeCount > 0 && (
                <span className="notification-badge">{detailsNoticeCount}</span>
              )}
            </div>
            {showPatientDiaryTab && (
              <div
                className={`doc-tab ${activeTab === 'PatientDiary' ? 'active' : ''} ${!doc.patientId ? 'disabled' : ''}`}
                onClick={() => doc.patientId && setActiveTab('PatientDiary')}
                title={!doc.patientId ? 'No patient matched' : undefined}
              >
                Patient Diary
                {pendingReferralsCount > 0 && (
                  <span className="notification-badge">{pendingReferralsCount}</span>
                )}
              </div>
            )}
            {isCodesTabView ? (
              <div
                className={`doc-tab ${activeTab === 'Codes' ? 'active' : ''}`}
                onClick={() => setActiveTab('Codes')}
              >
                Codes
              </div>
            ) : (
              <>
                <div
                  className={`doc-tab ${activeTab === 'ExistingCodes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('ExistingCodes')}
                >
                  Existing Codes
                </div>
                <div
                  className={`doc-tab ${activeTab === 'NewCodes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('NewCodes')}
                >
                  New Codes
                </div>
              </>
            )}
            <div
              className={`doc-tab ${activeTab === 'Measurements' ? 'active' : ''}`}
              onClick={() => setActiveTab('Measurements')}
            >
              Measurements
            </div>
            <div
              className={`doc-tab ${activeTab === 'OutcomeTasks' ? 'active' : ''}`}
              onClick={() => setActiveTab('OutcomeTasks')}
            >
              Outcome Tasks
            </div>
            <div
              className={`doc-tab ${activeTab === 'Actions' ? 'active' : ''}`}
              onClick={() => setActiveTab('Actions')}
            >
              Actions
            </div>
          </div>
          )}

          <div className="sidebar-single-layout" ref={sidebar.sidebarLayoutRef}>
          <div className={`tab-content-wrapper${isSplitLayout ? ' tab-content-wrapper--split' : ''}${isAllContentView ? ' tab-content-wrapper--all' : ''}${isCodesTabView && activeTab === 'Codes' ? ' tab-content-wrapper--codes' : ''}`} ref={sidebar.tabContentWrapperRef}>
            {/* Keep tabs mounted using CSS display toggle to avoid remounting on tab switch */}
            <SidebarTabPane title={splitTitle('Details', 'Details')} visible={isPaneVisible('Details')}>
              <DetailsTab
                doc={doc}
                patientData={patient.patientData}
                patientDataError={patient.patientDataError}
                isRegenerationEnabled={isRegenerationEnabled}
                isEditingEnabled={isEditingEnabled}
                handleRegenerationClick={handleRegenerationClick}
                onDocumentUpdate={updateDoc}
              />
            </SidebarTabPane>
            {showPatientDiaryTab && (
              <SidebarTabPane visible={isAllContentView || activeTab === 'PatientDiary'}>
                <PatientDiaryPanel
                  patientData={patient.patientData}
                  patientDataError={patient.patientDataError}
                  patientRecordData={patient.patientRecordData}
                  patientRecordDataError={patient.patientRecordDataError}
                  doc={doc}
                  onDocumentClick={sidebar.openDocumentPreview}
                  onReferralClick={referral.handleReferralClick}
                  layoutMode={PATIENT_DIARY_LAYOUT_TAB}
                  onLayoutModeChange={sidebar.handlePatientDiaryLayoutChange}
                  canEdit={editingEnabled}
                  existingPatientCodes={doc.existingPatientCodes}
                  onExistingCodeClick={sidebar.handleDiaryExistingCodeClick}
                  onRefreshPatientCodes={patient.handleRefreshPatientCodes}
                  isRefreshingPatientCodes={patient.refreshingPatientCodes}
                />
              </SidebarTabPane>
            )}
            <SidebarTabPane
              title={splitTitle('ExistingCodes', 'Existing Codes')}
              visible={isPaneVisible('ExistingCodes')}
              paneRef={sidebar.existingCodesPaneRef}
            >
              <DataTab
                section="existing"
                doc={doc}
                isRegenerationEnabled={isRegenerationEnabled}
                isEditingEnabled={isEditingEnabled}
                handleRegenerationClick={handleRegenerationClick}
                onPdfSearch={viewer.handlePdfSearch}
                onDocumentUpdate={updateDoc}
                highlightedExistingCodeId={sidebar.highlightedExistingCodeId}
              />
            </SidebarTabPane>
            <SidebarTabPane title={splitTitle('NewCodes', 'New Codes')} visible={isPaneVisible('NewCodes')}>
              <DataTab
                section="new"
                doc={doc}
                isRegenerationEnabled={isRegenerationEnabled}
                isEditingEnabled={isEditingEnabled}
                handleRegenerationClick={handleRegenerationClick}
                onPdfSearch={viewer.handlePdfSearch}
                onDocumentUpdate={updateDoc}
              />
            </SidebarTabPane>
            <SidebarTabPane title={splitTitle('Measurements', 'Measurements')} visible={isPaneVisible('Measurements')}>
              <MeasurementsTab
                doc={doc}
                isRegenerationEnabled={isRegenerationEnabled}
                isEditingEnabled={isEditingEnabled}
                handleRegenerationClick={handleRegenerationClick}
                onDocumentUpdate={updateDoc}
                shouldBundleBloodPressureReadings={shouldBundleBloodPressureReadings}
              />
            </SidebarTabPane>
            <SidebarTabPane title={splitTitle('OutcomeTasks', 'Outcome Tasks')} visible={isPaneVisible('OutcomeTasks')}>
              <OutcomeTasksTab
                doc={doc}
                activeUsers={activeUsers}
                teams={teams}
                isRegenerationEnabled={isRegenerationEnabled}
                isEditingEnabled={isEditingEnabled}
                handleRegenerationClick={handleRegenerationClick}
                onDocumentUpdate={updateDoc}
                onPdfSearch={viewer.handlePdfSearch}
              />
            </SidebarTabPane>
          </div>
          <div className={`sidebar-actions-pane${activeTab === 'Actions' ? ' is-visible' : ''}`}>
            <SidebarTabPane visible={activeTab === 'Actions'}>
              <ActionsTab
                doc={doc}
                activeUsers={activeUsers}
                teams={teams}
                patientData={patient.patientData}
                onDocumentUpdate={updateDoc}
                patientDiaryLayout={patientDiaryLayout}
                onPatientDiaryLayoutChange={sidebar.handlePatientDiaryLayoutChange}
                tabViewMode={tabViewMode}
                onTabViewModeChange={sidebar.handleTabViewModeChange}
              />
            </SidebarTabPane>
          </div>
          </div>
        </div>

        {patientDiaryLayout === PATIENT_DIARY_LAYOUT_SIDEBAR && sidebar.isPatientDiaryVisible && doc.patientId && (
          <div className="patient-diary-container">
            <PatientDiaryPanel
              patientData={patient.patientData}
              patientDataError={patient.patientDataError}
              patientRecordData={patient.patientRecordData}
              patientRecordDataError={patient.patientRecordDataError}
              doc={doc}
              onDocumentClick={sidebar.openDocumentPreview}
              onReferralClick={referral.handleReferralClick}
              layoutMode={PATIENT_DIARY_LAYOUT_SIDEBAR}
              onLayoutModeChange={sidebar.handlePatientDiaryLayoutChange}
              canEdit={editingEnabled}
              existingPatientCodes={doc.existingPatientCodes}
              onExistingCodeClick={sidebar.handleDiaryExistingCodeClick}
              onRefreshPatientCodes={patient.handleRefreshPatientCodes}
              isRefreshingPatientCodes={patient.refreshingPatientCodes}
            />
          </div>
        )}
      </div>

      {fileAway.showFileAwayModal && (
        <FileAwayModal
          doc={doc}
          activeUsers={activeUsers}
          teams={teams}
          comms={fileAway.comms}
          onConfirm={fileAway.handleFileAwayConfirm}
          onCancel={fileAway.handleFileAwayCancel}
          isProcessing={fileAway.fileAwayLoading}
          shouldBundleBloodPressureReadings={shouldBundleBloodPressureReadings}
        />
      )}

      {warnings.showViewersWarningModal && (
        <ViewersWarningModal
          viewers={getViewers(doc.documentId)}
          activeUsers={activeUsers}
          currentUser={user}
          onDismiss={warnings.dismissViewersWarning}
          onBackToDocuments={() => {
            warnings.dismissViewersWarning();
            navigation.handleBackToDocumentsList();
          }}
        />
      )}

      {/* Proactive pending referrals modal (once per document per session) */}
      {patient.showPendingReferralsModal && (
        <PendingReferralsModal
          referrals={patient.patientRecordData?.pendingReferrals || []}
          onReferralClick={referral.handleReferralClick}
          onDismiss={patient.dismissPendingReferrals}
        />
      )}

      {/* Email forwarding guidance — after pending referrals in DOM so it stacks on top when both open */}
      {warnings.showEmailForwardWarningModal && (
        <EmailForwardWarningModal onDismiss={warnings.dismissEmailForwardWarning} />
      )}

      {patient.showPatientStatusWarningModal && (
        <PatientStatusWarningModal
          status={patient.patientData?.status || 'ACTIVE'}
          patientName={patient.patientData?.name}
          onDismiss={patient.dismissPatientStatusWarning}
        />
      )}

      {referral.referralModalOpen && (
        <ReferralModal
          isOpen={referral.referralModalOpen}
          onClose={referral.closeReferralModal}
          referral={referral.selectedReferral}
          onMarkComplete={referral.handleReferralMarkComplete}
          onUpdate={referral.handleReferralUpdate}
          onDelete={referral.handleReferralDelete}
          loading={referral.referralModalLoading}
          isReadOnly={!hasDocsWrite || !!referral.selectedReferral?.isComplete}
          activeUsers={activeUsers}
          teams={teams}
        />
      )}

      {sidebar.showDocumentPreview && sidebar.previewDocumentId && (
        <DocumentPreviewModal
          isOpen={sidebar.showDocumentPreview}
          onClose={sidebar.closeDocumentPreview}
          documentId={sidebar.previewDocumentId}
          activeUsers={activeUsers}
          teams={teams}
        />
      )}

      {duplicates.showDuplicatesModal && duplicateDocumentIds.length > 0 && (
        <DocumentPreviewModal
          isOpen={duplicates.showDuplicatesModal}
          onClose={duplicates.closeDuplicatesModal}
          documentId={doc.documentId}
          documentIds={duplicateDocumentIds}
          activeUsers={activeUsers}
          teams={teams}
          viewMode="DUPLICATES"
          onDeleteOtherDoc={duplicates.removeDuplicate}
        />
      )}

      <DeleteModal
        isOpen={record.deleteModalOpen}
        onClose={record.closeDeleteModal}
        onConfirm={record.confirmDelete}
        document={doc}
        getAssigneeName={record.getAssigneeName}
      />
    </div>
  );
};

export default DocumentDetailView;
